<template>
  <div class="gs-app gs-refresh-controls gs-marketing-panel">
    <element-list
      v-for="list in lists"
      :key="list.key"
      :list-key="list.key"
      :title="list.title"
      :description="list.description"
      :icon="list.icon"
      :items="list.items"
      :add-label="list.addLabel"
      :empty-copy="list.emptyCopy"
      :fallback="list.fallback"
      @add="openDrawer(list.key)"
      @edit="editElement(list.key, $event)"
      @delete="askDelete($event)"
    />

    <element-drawer
      :open="drawer !== null"
      :type="drawerType"
      :editing="drawerEditing"
      :form="localmodel"
      :errors="drawerErrors"
      @close="closeDrawer()"
      @save="saveDrawer()"
    />

    <optin-form-modal
      :video="localModel"
      :model="localmodel"
      :submitOptionForm="submitOptionForm"
      :ctaIndex="ctaIndex"
      @saved="$emit('saved')"
    />

    <gs-alert-dialog
      v-model="deleteOpen"
      title="Delete this element?"
      copy="It stops showing on the video once you save."
      confirm-label="Delete"
      cancel-label="Keep it"
      variant="danger"
      @confirm="deleteCta(pendingDeleteId)"
      @cancel="pendingDeleteId = null"
    />
  </div>
</template>

<script>
import Notify from '@groovepages/gd-lib/notify';
import {
  required,
  minLength,
  maxLength,
  requiredIf,
  numeric,
  url,
} from 'vuelidate/lib/validators';
import OptinFormModal from '../optinFormModal.vue';
import ElementList from './ElementList.vue';
import ElementDrawer from './ElementDrawer.vue';
import { toSeconds, formatTime, parseTime } from './timeFields';

// The four element types live in one `call_to_actions` array and are told
// apart by `cta.type`. These are the stored values — the player reads them —
// so they are the keys everything else here hangs off.
const STORED_TYPE = {
  optin: 'Optin Form',
  banner: 'Banner',
  cta: 'Text Overlay With CTA',
  text: 'Text Overlay',
};

export default {
  name: 'MarketingPanel',

  // The shell hands every panel the same props; the ones this panel does not
  // declare would otherwise land on its root element.
  inheritAttrs: false,

  components: { ElementList, ElementDrawer, OptinFormModal },

  props: {
    model: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      localModel: this.model,

      // The draft every drawer edits. The opt-in modal is handed the same
      // object by reference, exactly as the old step did.
      localmodel: {
        hours: 0,
        minutes: 0,
        seconds: 0,
        ctaname: '',
        headingText: '',
        buttonText: '',
        ctaBannerImage: '',
        url: '',
        showSkipButton: false,
        hyperLinkCaption: false,
        till_seconds: 0,
        till_hours: 0,
        till_minute: 0,
        newTabToggle: true,
        optional_url: '',
      },

      // `getConfigTile` fills these from `call_to_actions`.
      optInForm: [],
      bannerForm: [],
      overlay: [],
      overlayCta: [],

      // `null`, or `{ type: 'banner' | 'text' | 'cta', editing }`.
      drawer: null,

      editCtaId: {},
      ctaIndex: 0,
      optinId: 0,
      bannerID: 0,
      overlayID: 0,
      overlayCapID: 0,

      time: '',
      seconds: 0,
      till_time: '',
      till_seconds: 0,

      checkBanner: false,
      sameTimeBanner: false,
      bannerImageTouched: false,

      deleteOpen: false,
      pendingDeleteId: null,
    };
  },

  validations: {
    localmodel: {
      ctaname: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(80),
      },
      optional_url: {
        required: requiredIf(function requiredWhenLinked() {
          return this.localmodel.hyperLinkCaption;
        }),
        url,
      },
      hours: {
        required,
        numeric,
        maxLength: maxLength(2),
      },
      minutes: {
        required,
        numeric,
        maxLength: maxLength(2),
      },
      seconds: {
        required,
        numeric,
        maxLength: maxLength(2),
      },
      till_hours: {
        required,
        numeric,
        maxLength: maxLength(2),
      },
      till_minute: {
        required,
        numeric,
        maxLength: maxLength(2),
      },
      till_seconds: {
        required,
        numeric,
        maxLength: maxLength(2),
      },
      headingText: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(80),
      },
      buttonText: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(30),
      },
      url: {
        required,
        url,
      },
    },
  },

  computed: {
    lists() {
      return [
        {
          key: 'optin',
          title: 'Opt-in form',
          description: 'Collect an email before the video continues.',
          icon: 'edit',
          addLabel: 'Add opt-in form',
          emptyCopy: 'No opt-in form yet. Viewers watch straight through.',
          fallback: 'Untitled form',
          items: this.optInForm.map(item => this.row(item, { primary: 'title' })),
        },
        {
          key: 'banner',
          title: 'Banners',
          description: 'Show an image with a link at a chosen time.',
          icon: 'image',
          addLabel: 'Add banner',
          emptyCopy: 'No banners yet.',
          fallback: 'Banner',
          items: this.bannerForm.map(item => this.row(item, { primary: 'url' })),
        },
        {
          key: 'cta',
          title: 'Calls to action',
          description: 'A heading and a button over the video.',
          icon: 'external-link',
          addLabel: 'Add call to action',
          emptyCopy: 'No calls to action yet.',
          fallback: 'Call to action',
          items: this.overlayCta.map(item =>
            this.row(item, { primary: 'heading', secondary: 'button_text' })
          ),
        },
        {
          key: 'text',
          title: 'Text overlays',
          description: 'A line of text with an optional link.',
          icon: 'heading',
          addLabel: 'Add text overlay',
          emptyCopy: 'No text overlays yet.',
          fallback: 'Text overlay',
          items: this.overlay.map(item => this.row(item, { primary: 'title' })),
        },
      ];
    },

    drawerType() {
      return this.drawer ? this.drawer.type : 'banner';
    },

    drawerEditing() {
      return Boolean(this.drawer && this.drawer.editing);
    },

    // The old modals leaned on `text-input`'s own validator slot; the drawer
    // takes plain strings, so the rules are turned into messages here.
    drawerErrors() {
      const rules = this.$v.localmodel;
      return {
        time: this.sameTimeBanner
          ? this.$t('frontend_groovevideo_videos_step3_banner_same_time')
          : '',
        image:
          this.bannerImageTouched && !this.localmodel.ctaBannerImage
            ? 'Choose a banner image.'
            : '',
        url: rules.url.$error ? 'Enter a full web address, starting with https://' : '',
        optional_url: rules.optional_url.$error
          ? 'Enter a full web address, starting with https://'
          : '',
        ctaname: rules.ctaname.$error ? 'Enter between 3 and 80 characters.' : '',
        headingText: rules.headingText.$error ? 'Enter between 3 and 80 characters.' : '',
        buttonText: rules.buttonText.$error ? 'Enter between 3 and 30 characters.' : '',
        till: '',
      };
    },
  },

  watch: {
    model(value) {
      this.localModel = value;
      this.getConfigTile(this.callToActions());
    },
    'localmodel.hours': function onHours() {
      this.checkBannerTime();
    },
    'localmodel.minutes': function onMinutes() {
      this.checkBannerTime();
    },
    'localmodel.seconds': function onSeconds() {
      this.checkBannerTime();
    },
  },

  created() {
    this.getConfigTile(this.callToActions());
  },

  methods: {
    // The shell asks every panel this before it saves. There is nothing to
    // validate once the drawers are closed — each one validates on its own
    // Save — so the only failing case is a half-filled drawer left open.
    validate() {
      let isValid = true;
      if (this.drawer && !this.isDrawerValid()) {
        Notify.fail('Fix the fields in the element you are editing, or cancel it');
        isValid = false;
      }
      this.$emit('on-validate', isValid);
      return isValid;
    },

    isDrawerValid() {
      const rules = this.$v.localmodel;
      const timesValid =
        !rules.hours.$invalid && !rules.minutes.$invalid && !rules.seconds.$invalid;
      const tillValid =
        !rules.till_hours.$invalid &&
        !rules.till_minute.$invalid &&
        !rules.till_seconds.$invalid;

      if (this.drawer.type === 'banner') {
        return timesValid && !rules.url.$invalid && Boolean(this.localmodel.ctaBannerImage);
      }
      if (this.drawer.type === 'text') {
        return (
          timesValid && tillValid && !rules.ctaname.$invalid && !rules.optional_url.$invalid
        );
      }
      return (
        timesValid &&
        tillValid &&
        !rules.headingText.$invalid &&
        !rules.buttonText.$invalid &&
        !rules.url.$invalid
      );
    },

    callToActions() {
      return Array.isArray(this.localModel.call_to_actions)
        ? this.localModel.call_to_actions
        : [];
    },

    // A video saved before it had any elements can come back without the
    // array. Creating it is not an edit the user made, so the shell is told to
    // move that key onto its saved baseline.
    ensureList() {
      if (!Array.isArray(this.localModel.call_to_actions)) {
        this.$set(this.localModel, 'call_to_actions', []);
        this.$emit('patch-snapshot', ['call_to_actions']);
      }
      return this.localModel.call_to_actions;
    },

    listFor(type) {
      if (type === 'optin') return this.optInForm;
      if (type === 'banner') return this.bannerForm;
      if (type === 'cta') return this.overlayCta;
      return this.overlay;
    },

    // `{ id, primary, secondary, timing }` — what `ElementList` renders.
    row(item, keys) {
      const { cta } = item;
      return {
        id: cta.index,
        primary: cta[keys.primary] || cta.title || cta.url || '',
        secondary: keys.secondary ? cta[keys.secondary] : '',
        timing: this.timing(cta),
      };
    },

    timing(cta) {
      const from = formatTime(
        typeof cta.seconds === 'number' ? cta.seconds : toSeconds(parseTime(cta.time))
      );
      if (cta.till_time === undefined || cta.till_time === null || cta.till_time === '') {
        return from;
      }
      const till = formatTime(
        typeof cta.till_seconds === 'number'
          ? cta.till_seconds
          : toSeconds(parseTime(cta.till_time))
      );
      return `${from} – ${till}`;
    },

    // Ported from step3's `changeTimeFormat` + `hoursToSeconds` pair, which
    // wrote the same two shapes of the same value.
    readFromTime() {
      this.seconds = toSeconds({
        hours: this.localmodel.hours,
        minutes: this.localmodel.minutes,
        seconds: this.localmodel.seconds,
      });
      this.time = formatTime(this.seconds);
    },

    readTillTime() {
      this.till_seconds = toSeconds({
        hours: this.localmodel.till_hours,
        minutes: this.localmodel.till_minute,
        seconds: this.localmodel.till_seconds,
      });
      this.till_time = formatTime(this.till_seconds);
    },

    setTimeFromString(cta) {
      const from = parseTime(cta.time);
      this.localmodel.hours = from.hours;
      this.localmodel.minutes = from.minutes;
      this.localmodel.seconds = from.seconds;

      if (cta.till_time) {
        const till = parseTime(cta.till_time);
        this.localmodel.till_hours = till.hours;
        this.localmodel.till_minute = till.minutes;
        this.localmodel.till_seconds = till.seconds;
      }

      this.localmodel.newTabToggle =
        cta.new_tab_toggle === undefined ? true : cta.new_tab_toggle;
    },

    // Two banners at the same second would stack on the player, so the second
    // one is refused. Ported verbatim from step3.
    checkBannerTime() {
      if (!this.checkBanner) return;
      this.readFromTime();
      if (!this.time) return;

      this.sameTimeBanner = this.callToActions().some(
        (element, index) =>
          element.cta.type === STORED_TYPE.banner &&
          element.cta.time === this.time &&
          this.editCtaId.index !== index
      );
    },

    resetForm() {
      this.localmodel.hours = 0;
      this.localmodel.minutes = 0;
      this.localmodel.seconds = 0;
      this.localmodel.till_hours = 0;
      this.localmodel.till_minute = 0;
      this.localmodel.till_seconds = 0;
      this.localmodel.headingText = '';
      this.localmodel.ctaname = '';
      this.localmodel.buttonText = '';
      this.localmodel.optional_url = '';
      this.localmodel.url = '';
      this.localmodel.ctaBannerImage = '';
      this.localmodel.hyperLinkCaption = false;
      this.localmodel.showSkipButton = false;
      this.localmodel.newTabToggle = true;

      this.editCtaId = {};
      this.checkBanner = false;
      this.sameTimeBanner = false;
      this.bannerImageTouched = false;
      this.seconds = 0;
      this.time = '';
      this.till_seconds = 0;
      this.till_time = '';
      this.$v.$reset();
    },

    // Ported from step3's `optinModalOpen`.
    openDrawer(type) {
      this.resetForm();

      if (type === 'optin') {
        this.ctaIndex = 0;
        this.$root.$emit('modal-open', 'optinForm');
        return;
      }

      this.drawer = { type, editing: false };
    },

    closeDrawer() {
      this.drawer = null;
      this.resetForm();
    },

    // Ported from step3's `editCta`, addressed by the stored `cta.index`
    // rather than a row position.
    editElement(type, id) {
      const list = this.listFor(type);
      const position = list.findIndex(item => item.cta.index === id);
      const arrayIndex = this.callToActions().findIndex(item => item.cta.index === id);
      if (position < 0 || arrayIndex < 0) return;

      this.resetForm();
      this.editCtaId = { indexId: id, index: arrayIndex };

      const { cta } = list[position];
      this.setTimeFromString(cta);

      if (type === 'optin') {
        this.localmodel.headingText = cta.title ? cta.title : '';
        this.localmodel.showSkipButton = cta.show_skip_button;
        this.ctaIndex = cta.index;
        this.$root.$emit('modal-open', 'optinForm');
        return;
      }

      if (type === 'banner') {
        this.localmodel.url = cta.url;
        this.localmodel.showSkipButton = cta.show_skip_button;
        this.localmodel.ctaBannerImage = cta.imgName;
      } else if (type === 'text') {
        this.localmodel.ctaname = cta.title;
        this.localmodel.hyperLinkCaption = cta.is_link_activated;
        this.localmodel.optional_url = cta.cta_caption_url;
      } else {
        this.localmodel.headingText = cta.heading;
        this.localmodel.buttonText = cta.button_text;
        this.localmodel.url = cta.url;
      }

      this.drawer = { type, editing: true };
    },

    saveDrawer() {
      if (!this.drawer) return;
      if (this.drawer.type === 'banner') this.submitBanner();
      else if (this.drawer.type === 'text') this.submitCaption();
      else this.captionButton();
    },

    // Every save path ends the same way: the shell owns the model and the
    // preview, so it is told both changed.
    commit() {
      this.getConfigTile(this.callToActions());
      this.$emit('updateModel', this.localModel);
      this.$emit('forceRerender');
    },

    /* Opt-in form. Called by `optinFormModal`, which owns its own modal and
       its integrations, and saves the video itself. */
    // eslint-disable-next-line max-lines-per-function
    async submitOptionForm(optin_id, closed = true) {
      this.$v.localmodel.headingText.$touch();
      this.$v.localmodel.hours.$touch();
      this.$v.localmodel.minutes.$touch();
      this.$v.localmodel.seconds.$touch();
      this.optinId = Math.floor(Math.random() * 10000 + 1);

      if (
        !this.$v.localmodel.headingText.$invalid &&
        !this.$v.localmodel.hours.$invalid &&
        !this.$v.localmodel.minutes.$invalid &&
        !this.$v.localmodel.seconds.$invalid
      ) {
        this.$emit('setIsLoading', true);
        this.readFromTime();
        const list = this.ensureList();

        if (this.editCtaId.index >= 0) {
          const index = this.editCtaId.index;
          list[index].cta.title = this.localmodel.headingText;
          list[index].cta.time = this.time;
          list[index].cta.seconds = this.seconds;
          list[index].cta.show_skip_button = this.localmodel.showSkipButton;
          this.getConfigTile(list);
        } else {
          const ctaField = {
            cta: {
              cta: true,
              cta_banner: false,
              cta_caption: false,
              cta_caption_button: false,
              cta_form: true,
              event: 'opt-in',
              seconds: this.seconds,
              show_skip_button: this.localmodel.showSkipButton,
              time: this.time,
              title: this.localmodel.headingText,
              type: STORED_TYPE.optin,
              video_id: this.$route.params.id,
            },
          };

          if (optin_id != 0) {
            ctaField.cta.index = optin_id;
            const optinIndex = this.optInForm.findIndex(el => el.cta.index == optin_id);
            this.$set(this.optInForm, optinIndex, ctaField);
            const localModelIndex = list.findIndex(el => el.cta.index == optin_id);
            this.$set(list, localModelIndex, ctaField);
          } else {
            ctaField.cta.index = this.optinId;
            this.optInForm.push(ctaField);
            list.push(ctaField);
          }
          this.ctaIndex = ctaField.cta.index;
        }

        if (closed) {
          this.localmodel.hours = 0;
          this.localmodel.minutes = 0;
          this.localmodel.seconds = 0;
          this.localmodel.headingText = '';
          this.localmodel.showSkipButton = false;
          this.$v.localmodel.headingText.$reset();
          this.$root.$emit('modal-close', 'optinForm');
          this.commit();
        }
        this.$emit('setIsLoading', false);
      }

      this.editCtaId = {};
      this.seconds = 0;
      this.time = '';
    },

    /* Banner. */
    // eslint-disable-next-line max-lines-per-function
    submitBanner() {
      this.bannerImageTouched = true;
      if (!this.localmodel.ctaBannerImage) return;

      this.$v.localmodel.hours.$touch();
      this.$v.localmodel.minutes.$touch();
      this.$v.localmodel.seconds.$touch();
      this.$v.localmodel.url.$touch();

      if (
        this.$v.localmodel.hours.$invalid ||
        this.$v.localmodel.minutes.$invalid ||
        this.$v.localmodel.seconds.$invalid
      ) {
        return;
      }

      this.checkBanner = true;
      this.checkBannerTime();

      if (this.$v.localmodel.url.$invalid || this.sameTimeBanner) return;

      const list = this.ensureList();
      this.bannerID = Math.floor(Math.random() * 10000 + 1);

      if (this.editCtaId.index >= 0) {
        const index = this.editCtaId.index;
        list[index].cta.time = this.time;
        list[index].cta.seconds = this.seconds;
        list[index].cta.show_skip_button = this.localmodel.showSkipButton;
        list[index].cta.imgName = this.localmodel.ctaBannerImage;
        list[index].cta.url = this.localmodel.url;
        list[index].cta.new_tab_toggle = this.localmodel.newTabToggle;
      } else {
        list.push({
          cta: {
            cta: true,
            cta_banner: true,
            cta_caption: false,
            cta_caption_button: false,
            cta_form: false,
            event: 'banner',
            index: `banner${this.bannerID}`,
            imgName: this.localmodel.ctaBannerImage,
            url: this.localmodel.url,
            new_tab_toggle: this.localmodel.newTabToggle,
            seconds: this.seconds,
            show_skip_button: this.localmodel.showSkipButton,
            time: this.time,
            type: STORED_TYPE.banner,
            video_id: this.$route.params.id,
          },
        });
      }

      this.commit();
      this.drawer = null;
      this.resetForm();
    },

    /* Text overlay. */
    // eslint-disable-next-line max-lines-per-function
    submitCaption() {
      this.$v.localmodel.ctaname.$touch();
      this.$v.localmodel.hours.$touch();
      this.$v.localmodel.minutes.$touch();
      this.$v.localmodel.seconds.$touch();
      this.$v.localmodel.till_hours.$touch();
      this.$v.localmodel.till_minute.$touch();
      this.$v.localmodel.till_seconds.$touch();
      this.$v.localmodel.optional_url.$touch();
      this.overlayID = Math.floor(Math.random() * 10000 + 1);

      if (
        this.$v.localmodel.ctaname.$invalid ||
        this.$v.localmodel.hours.$invalid ||
        this.$v.localmodel.minutes.$invalid ||
        this.$v.localmodel.seconds.$invalid ||
        this.$v.localmodel.till_hours.$invalid ||
        this.$v.localmodel.till_minute.$invalid ||
        this.$v.localmodel.till_seconds.$invalid ||
        this.$v.localmodel.optional_url.$invalid
      ) {
        return;
      }

      this.readFromTime();
      this.readTillTime();
      const list = this.ensureList();

      if (this.editCtaId.index >= 0) {
        const index = this.editCtaId.index;
        list[index].cta.time = this.time;
        list[index].cta.seconds = this.seconds;
        list[index].cta.till_time = this.till_time;
        list[index].cta.till_seconds = this.till_seconds;
        list[index].cta.is_link_activated = this.localmodel.hyperLinkCaption;
        list[index].cta.title = this.localmodel.ctaname;
        list[index].cta.cta_caption_url = this.localmodel.optional_url;
        list[index].cta.new_tab_toggle = this.localmodel.newTabToggle;
      } else {
        list.push({
          cta: {
            cta: true,
            cta_banner: false,
            cta_caption: true,
            cta_caption_button: false,
            cta_form: false,
            event: 'caption',
            index: `overlay${this.overlayID}`,
            cta_caption_url: this.localmodel.optional_url,
            new_tab_toggle: this.localmodel.newTabToggle,
            is_link_activated: this.localmodel.hyperLinkCaption,
            seconds: this.seconds,
            time: this.time,
            title: this.localmodel.ctaname,
            till_time: this.till_time,
            till_seconds: this.till_seconds,
            type: STORED_TYPE.text,
            video_id: this.$route.params.id,
          },
        });
      }

      this.commit();
      this.drawer = null;
      this.resetForm();
    },

    /* Call to action: a text overlay carrying a button. */
    // eslint-disable-next-line max-lines-per-function
    captionButton() {
      this.$v.localmodel.hours.$touch();
      this.$v.localmodel.minutes.$touch();
      this.$v.localmodel.seconds.$touch();
      this.$v.localmodel.till_hours.$touch();
      this.$v.localmodel.till_minute.$touch();
      this.$v.localmodel.till_seconds.$touch();
      this.$v.localmodel.headingText.$touch();
      this.$v.localmodel.buttonText.$touch();
      this.$v.localmodel.url.$touch();

      if (
        this.$v.localmodel.hours.$invalid ||
        this.$v.localmodel.minutes.$invalid ||
        this.$v.localmodel.seconds.$invalid ||
        this.$v.localmodel.till_hours.$invalid ||
        this.$v.localmodel.till_minute.$invalid ||
        this.$v.localmodel.till_seconds.$invalid ||
        this.$v.localmodel.url.$invalid ||
        this.$v.localmodel.headingText.$invalid ||
        this.$v.localmodel.buttonText.$invalid
      ) {
        return;
      }

      this.overlayCapID = Math.floor(Math.random() * 10000 + 1);
      this.readFromTime();
      this.readTillTime();
      const list = this.ensureList();

      if (this.editCtaId.index >= 0) {
        const index = this.editCtaId.index;
        list[index].cta.time = this.time;
        list[index].cta.seconds = this.seconds;
        list[index].cta.till_time = this.till_time;
        list[index].cta.till_seconds = this.till_seconds;
        list[index].cta.heading = this.localmodel.headingText;
        list[index].cta.button_text = this.localmodel.buttonText;
        list[index].cta.url = this.localmodel.url;
        list[index].cta.new_tab_toggle = this.localmodel.newTabToggle;
      } else {
        list.push({
          cta: {
            cta: true,
            cta_banner: false,
            cta_caption: false,
            cta_caption_button: true,
            cta_form: false,
            event: 'caption button',
            index: `overlaycaption${this.overlayCapID}`,
            url: this.localmodel.url,
            new_tab_toggle: this.localmodel.newTabToggle,
            seconds: this.seconds,
            show_skip_button: this.localmodel.showSkipButton,
            time: this.time,
            heading: this.localmodel.headingText,
            button_text: this.localmodel.buttonText,
            till_seconds: this.till_seconds,
            till_time: this.till_time,
            type: STORED_TYPE.cta,
            video_id: this.$route.params.id,
          },
        });
      }

      this.commit();
      this.drawer = null;
      this.resetForm();
    },

    askDelete(id) {
      this.pendingDeleteId = id;
      this.deleteOpen = true;
    },

    // Ported from step3's `deleteCta`: the row is addressed by `cta.index`.
    deleteCta(id) {
      this.pendingDeleteId = null;
      const list = this.callToActions();
      const arrayIndex = list.findIndex(item => item.cta.index === id);
      if (arrayIndex < 0) return;

      list.splice(arrayIndex, 1);
      this.commit();
    },

    // Ported from step3: one array in, four lists out.
    getConfigTile(callToActions) {
      const items = Array.isArray(callToActions) ? callToActions : [];
      this.optInForm = [];
      this.bannerForm = [];
      this.overlay = [];
      this.overlayCta = [];

      for (let i = 0; i < items.length; i += 1) {
        const { type } = items[i].cta;
        if (type == STORED_TYPE.optin) {
          this.optInForm.push(items[i]);
        } else if (type == STORED_TYPE.banner) {
          this.bannerForm.push(items[i]);
        } else if (type == STORED_TYPE.text) {
          this.overlay.push(items[i]);
        } else {
          this.overlayCta.push(items[i]);
        }
      }
    },

    // Kept from step3. The shell currently owns the preview and ignores this,
    // but the panel still says when the user asked to watch.
    playVideo() {
      this.$emit('preview');
    },
  },
};
</script>

<style scoped>
.gs-marketing-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px;
}

@media (max-width: 640px) {
  .gs-marketing-panel {
    padding: 14px;
  }
}
</style>
